// src/pages/BookingDetails.js - Dedicated Booking Details Page
import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Button,
  Spin,
  notification
} from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { bookingsDB } from '../services/firebase/database';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { useTheme } from '../components/ParticleBackground';
import { useNavigate, useParams } from 'react-router-dom';
import BookingDetailsContent from './BookingDetailsContent';

const { Title, Text } = Typography;

const BookingDetails = () => {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const { user: currentUser } = useAuth();
  const { theme, getThemeStyles } = useTheme();
  const navigate = useNavigate();
  const { bookingId } = useParams();

  useEffect(() => {
    if (!currentUser) {
      navigate('/signin');
      return;
    }
    if (!bookingId) {
      navigate('/my-bookings');
      return;
    }
    loadBookingDetails();
  }, [currentUser, bookingId, navigate]);

  const loadBookingDetails = async () => {
    setLoading(true);
    try {
      // Get the specific booking
      const userBookings = await bookingsDB.getByUserId(currentUser.id);
      const selectedBooking = userBookings.find(b => b.id === bookingId);

      if (!selectedBooking) {
        notification.error({
          message: 'Booking Not Found',
          description: 'The requested booking could not be found.'
        });
        navigate('/my-bookings');
        return;
      }

      // Fetch payment status for the booking
      try {
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('bookingId', '==', selectedBooking.id)
        );
        const paymentSnapshot = await getDocs(paymentsQuery);

        let paymentStatus = 'unpaid';
        let paymentDetails = null;

        if (!paymentSnapshot.empty) {
          const paymentDoc = paymentSnapshot.docs[0];
          paymentDetails = { id: paymentDoc.id, ...paymentDoc.data() };
          paymentStatus = paymentDetails.status || 'pending';
        }

        setBooking({
          ...selectedBooking,
          paymentStatus,
          paymentDetails
        });
      } catch (error) {
        console.error('Error fetching payment for booking:', selectedBooking.id, error);
        setBooking({
          ...selectedBooking,
          paymentStatus: 'unpaid',
          paymentDetails: null
        });
      }
    } catch (error) {
      console.error('Error loading booking details:', error);
      notification.error({
        message: 'Error Loading Booking',
        description: 'There was an error loading the booking details.'
      });
      navigate('/my-bookings');
    } finally {
      setLoading(false);
    }
  };

  const themeStyles = getThemeStyles();

  // Detect if user is on mobile device
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  };

  if (loading) {
    return (
      <div style={{
        background: themeStyles.background,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div style={{
        background: themeStyles.background,
        minHeight: '100vh',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ textAlign: 'center' }}>
          <Title level={3}>Booking Not Found</Title>
          <Text>The requested booking could not be found.</Text>
          <div style={{ marginTop: '20px' }}>
            <Button type="primary" onClick={() => navigate('/my-bookings')}>
              Back to My Bookings
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <BookingDetailsContent
      booking={booking}
      theme={theme}
      themeStyles={themeStyles}
      isMobile={isMobileDevice()}
      onBack={() => navigate('/my-bookings')}
    />
  );
};
export default BookingDetails;